
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import Toast from '@/utils/toast';
import DatePicker from '@/components/shared/DatePicker.vue';
import FullPageSpinner from '@/components/shared/suites/FullPageSpinner.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import SelectComponent from '@/components/shared/Select.vue';

const namespace: string = 'auth';

@Component<DuplicateMenusModal>({
	components: {
		SelectComponent,
		DatePicker,
		FullPageSpinner,
		InfoBanner,
		StandardModal
	}
})
export default class DuplicateMenusModal extends Vue {
	@Action('bulkCreateRotationalMenus', { namespace: 'menus' }) private bulkCreateRotationalMenus!: (payload: RotationalMenuPayload[]) => Promise<void>;
	@Getter('getTimezone', { namespace }) private timezone!: string;
	@Getter('getHolidayHours', { namespace }) private holidayHours!: HolidayHours[];
	@Prop({ type: Boolean, required: true }) private showModal!: boolean;
	@Prop({ type: Array, required: true }) private dailyMenus!: DailyMenu[];
	@Prop({ type: Boolean, required: true }) private hasWeekendOrdering!: boolean;

	private formattedItems: RotationalMenuPayloadItem[][] = [];
	private weeks: number = 1;
	private endDate: string = '';
	private duplicatingMenus: boolean = false;

	private weekOptions: { text: string; value: number}[] = [
		{
			text: this.$tc('menu.editor.availability.rotational_menus.weeks_option', 1, { n: 1 }),
			value: 1
		},
		{
			text: this.$tc('menu.editor.availability.rotational_menus.weeks_option', 2, { n: 2 }),
			value: 2
		},
		{
			text: this.$tc('menu.editor.availability.rotational_menus.weeks_option', 3, { n: 3 }),
			value: 3
		},
		{
			text: this.$tc('menu.editor.availability.rotational_menus.weeks_option', 4, { n: 4 }),
			value: 4
		},
		{
			text: this.$tc('menu.editor.availability.rotational_menus.weeks_option', 5, { n: 5 }),
			value: 5
		}
	];

	private get dateString(): string {
		return this.$t('menu.editor.availability.rotational_menus.duplicate_date', { date: DateTime.fromISO(this.dailyMenus[0].date).setLocale(this.$i18n.locale).toFormat('DDD') });
	}

	/**
	 * Get the minimum date that can be selected, this depends the frequency of duplication
	 * This prevents selecting a date that wouldn't duplicate any menus
	 * ex: if duplicating every 2 weeks, you can't select the next Sunday as the end date
	 *
	 */
	private get minDate(): string {
		return DateTime.fromISO(this.dailyMenus[6].date).plus({ weeks: this.weeks }).toISODate();
	}

	private get maxDate(): string {
		return DateTime.fromISO(this.dailyMenus[6].date).plus({ weeks: 53 }).toISODate();
	}

	private created(): void {
		this.formatMenuItems();
	}

	/**
	 * Format the rotational menu items for each day of the week to match the expected payload
	 *
	 * @return {void}
	 */
	private formatMenuItems(): void {
		this.formattedItems = this.dailyMenus.map((dailyMenu: DailyMenu) => {
			return dailyMenu.menus.flatMap((menu: Menu) => {
				const menuItems = menu.items?.map((item: MenuItem) => {
					// rotational_menu_id is null because these are items that will be added to new menus
					// Remy will handle adding the rotational_menu_id if a menu is being updated
					return {
						rotational_menu_id: null,
						menu_id: menu.id,
						section_item_id: item.section_id ? item.id : null,
						menu_item_id: item.section_id ? null : item.id,
						section_id: item.section_id || null
					} as RotationalMenuPayloadItem;
				}) || [];
				const sectionItems = menu.sections?.length ? menu.sections.flatMap((section: MenuSection) => {
					return section.items?.map((item: MenuItem) => {
						return {
							rotational_menu_id: null,
							menu_id: menu.id,
							section_item_id: item.id,
							menu_item_id: null,
							section_id: section.id
						} as RotationalMenuPayloadItem;
					}) || [];
				}) : [];
				return [...menuItems, ...sectionItems];
			});
		});
	}

	/**
	 * Duplicate the menus for the selected number of weeks until the end date
	 * This will create new menus for each day of the week or update existing menus
	 *
	 * @return {void}
	 */
	private async duplicateMenus(): Promise<void> {
		this.duplicatingMenus = true;
		const menus: RotationalMenuPayload[] = [];
		const endDate: DateTime = DateTime.fromISO(this.endDate);
		const now: DateTime = DateTime.now().setZone(this.timezone);
		let endOfCurrentWeek: DateTime = DateTime.fromISO(this.dailyMenus[6].date);

		// Loop until the endDate is reached, increasing the endOfCurrentWeek by the number of weeks selected each iteration
		for (let i = 1; endOfCurrentWeek <= endDate; i++) {
			endOfCurrentWeek = endOfCurrentWeek.plus({ weeks: this.weeks });
			if (endOfCurrentWeek > endDate) {
				continue;
			}

			// Loop through each day's menus until the end week is reached
			for (const dailyMenu of this.dailyMenus) {
				const menuDate: DateTime = DateTime.fromISO(dailyMenu.date);
				const dayOfWeek: number = menuDate.weekday;

				// menuDate will always be a date in the current date range
				// ex: when duplicating menus from 2024/09/30 - 2024/10/06, Monday will always be 2024/09/30, Tuesday 2024/10/01, etc.
				// So we need to multiply the number of weeks by the number of iterations to get the correct date in the future
				const updatedMenuDate: DateTime = menuDate.plus({ weeks: this.weeks * i });

				// Skip weekends if weekend ordering is disabled
				if (!this.hasWeekendOrdering && (dayOfWeek === 6 || dayOfWeek === 7)) {
					continue;
				}

				// Skip if the menu date is in the past
				// days will be > 0 if the date is in today or the future
				if (now.startOf('day').diff(updatedMenuDate.startOf('day'), 'days').days > 0) {
					continue;
				}

				// Skip if the menu date is a holiday
				if (this.holidayHours.find((holiday: HolidayHours) => updatedMenuDate.toISODate() === holiday.date)) {
					continue;
				}

				menus.push({
					date: updatedMenuDate.toISODate(),
					items: this.formattedItems[dayOfWeek - 1]
				});
			}
		}
		try {
			await this.bulkCreateRotationalMenus(menus);
			this.$emit('close');
		}
		catch (errorMessage) {
			Toast.error(this.$t('menu.editor.availability.rotational_menus.error_duplicating_menus', { errorMessage }));
		}
		finally {
			this.duplicatingMenus = false;
		}
	}

	/**
	 * Only allow Sundays to be selected as the end date
	 *
	 * @param {string} date
	 * @return {boolean}
	 */
	private allowedDates(date: string): boolean {
		return DateTime.fromISO(date).weekday === 7;
	}
}
